import { useEffect, useRef, useState } from 'react';
import Dexie from 'dexie';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  CUENTAS_BANCO,
  DEFAULT_SETTINGS,
  centavos,
  esCuentaEnDolares,
  fechaEnNicaragua,
  formatMoney,
  fxRate,
  hoy,
  parseMoney,
  usdToCordobas,
  type Centavos,
  type CuentaBanco,
  type CuentaBancoCordobas,
} from '@abastece/shared';
import {
  ACCOUNTS,
  responsableDe,
  yaContada,
  type Deposito,
  type Gasto,
  type MovimientoDueno,
  type PagoProveedor,
  type SesionCaja,
} from '@abastece/core';
import { db } from '../db/dexie.js';
import { leerSinEfecto, tuvoEfecto } from '../lib/sinEfecto.js';
import { readSettings } from '../db/reader.js';
import { cuentaEnCordobas, cuentasEnUso, nombrePuesto, saldosDeBancos } from '../lib/bancos.js';
import {
  porPagar,
  porPagarPronto,
  saldoDelEstadoEnDolares,
  semanaPorCuadrar,
  totalDeSobres,
  type PorPagar,
} from '../lib/cuadrar.js';
import { pideReconteo } from '../lib/dia.js';
import { emitirYVer } from '../lib/emitir.js';
import { atrasHasta, irA } from '../lib/volver.js';
import { pagoDeLaDeuda } from '../lib/gastos.js';
import { otrosUsos, useUsosDeReferencias, yaPago } from '../lib/referencias.js';
import { usePuede } from '../lib/sesion.js';
import { etiquetaCategoriaGasto } from '../i18n/dominio.js';
import { useT } from '../i18n/index.js';
import { Asistente, Respuestas, indicePaso, type Paso } from '../ui/Asistente.js';
import { Campo } from '../ui/Campo.js';
import { ElegirCuenta } from '../ui/ElegirCuenta.js';
import { AnularDeposito, AnularGasto, AnularMovimiento, AnularPago, ExplicarFaltante, faltanteDelConteo } from '../ui/Correcciones.js';
import { Dato, DatoTexto, Veredicto, enDolares, fechaCorta } from '../ui/CuentasDeCaja.js';
import { Ico } from '../ui/Iconos.js';
import { ListaDeAvisos, usePendientesDelDueno, type Aviso } from '../ui/LoDelDia.js';
import { LoQueDejoElDia } from '../ui/LoQueDejoElDia.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { SIN_CONTAR, contado, pasoDeConteo, primerConteo, type Contando } from '../ui/PasoDeConteo.js';
import { usePasosDeDeposito } from '../ui/PasosDeDeposito.js';
import { usePasosDeGasto } from '../ui/PasosDeGasto.js';
import { usePasosDelDueno } from '../ui/PasosDelDueno.js';
import { usePasosDePago } from '../ui/PasosDePago.js';
import { diasDesde, pagosEnCola, type EnCola, destinoDe } from './Transferencias.js';

/**
 * «Cuadrar la semana»: the owner going over the week's money, a step at a time (`/dinero/semana`).
 *
 * The cashiers count every shift; the owner goes over the money once a week (the owner,
 * 2026-10-03). In the order that makes the safe's count come out right: the envelopes the tills
 * left, counted all together, and each on its own only when the total is off; everything that
 * left the safe and is not written down yet (expenses, deposits, money for the owner, payments to
 * suppliers); the safe, counted blind, its first count where it starts; the transfers seen in the
 * bank; the statements of the bank, the dollar account and the wallet; what is owed to suppliers
 * soon; and what the week left. The week is gone over (`semana.cuadrada`) on the last step, and the
 * home screen asks again a week later.
 *
 * What waited when the steps began is kept as it was (`inicio`): an envelope received or a
 * transfer seen to drops out of the lists the moment it is done, and its step has to stay to say
 * how it went.
 */

interface Recibido {
  esperadoC: Centavos;
  contadoC: Centavos;
  esperadoD: Centavos;
  contadoD: Centavos;
  primeroC: Centavos | null;
  primeroD: Centavos | null;
  /** Received on another phone while it was counted here: nothing was left to receive. */
  enOtro?: true;
  /** The receipt, and what the books found missing at it, to be explained if it was spent. */
  eventoId?: string;
  falto?: Centavos;
}

/** Something these steps list being voided: written down wrongly, here or before. */
type Anulando =
  | { tipo: 'gasto'; gasto: Gasto }
  | { tipo: 'deposito'; deposito: Deposito }
  | { tipo: 'dueno'; movimiento: MovimientoDueno }
  | { tipo: 'pago'; deuda: { tipo: 'compra' | 'gasto'; id: string }; pago: PagoProveedor; nombre: string };

type Transferencia = 'llego' | 'espera' | 'fiado' | 'perdida';
/** What left the safe and is being written down, inside the steps. */
type Anotando = 'gasto' | 'deposito' | 'dueno' | 'pago';
/** The accounts with a statement to square: each bank account (`bancos.ts`), and the wallet. */
type Cuenta = CuentaBanco | 'BILLETERA_MOVIL';
const CODIGOS = [ACCOUNTS.CAJA_FUERTE.code, ACCOUNTS.BILLETERA_MOVIL.code, ...CUENTAS_BANCO.map((c) => ACCOUNTS[c].code)];

const VACIO_CONTEO = { cordobas: SIN_CONTAR, dolares: SIN_CONTAR };
const conEfectivo = (s: SesionCaja) => (s.porRecibir ?? 0) > 0 || (s.porRecibirDolares ?? 0) > 0;

export function CuadrarSemana() {
  const t = useT();
  const puede = usePuede('recibir_efectivo');
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const usuarios = useLiveQuery(() => db.usuarios.toArray(), [], []);
  const clientes = useLiveQuery(() => db.clientes.toArray(), [], []);
  const usos = useUsosDeReferencias();
  const pendientes = usePendientesDelDueno();

  const ahora = useLiveQuery(
    async () => {
      const [sesiones, cola, semanas, arqueos, sinEfecto] = await Promise.all([
        db.sesiones.toArray(),
        pagosEnCola(),
        db.eventos.where('type').equals('semana.cuadrada').toArray(),
        db.eventos.where('type').equals('efectivo.arqueo').toArray(),
        leerSinEfecto(),
      ]);
      const ultimo =
        semanas
          .filter((e) => tuvoEfecto(e, sinEfecto))
          .map((e) => (e.payload as { hasta: string }).hasta)
          .sort()
          .at(-1) ?? null;
      return {
        cajas: sesiones.filter(conEfectivo).sort((a, b) => a.abiertaEn.localeCompare(b.abiertaEn)),
        abiertas: sesiones.filter((s) => s.estado === 'abierta'),
        cola,
        ultimo,
        /** Never counted before: this count is where the safe starts (`inicial`). */
        primerArqueo: !arqueos.some((e) => tuvoEfecto(e, sinEfecto)),
      };
    },
    [],
    undefined,
  );
  /** What waited when the steps began, kept: see the comment above. */
  const [inicio, setInicio] = useState<NonNullable<typeof ahora> | null>(null);
  useEffect(() => {
    if (ahora && !inicio) setInicio(ahora);
  }, [ahora, inicio]);

  /** The books' balances the safe and the statements are held against, as they stand now. */
  const libros = useLiveQuery(async () => {
    const s: Record<string, number> = {};
    for (const cuenta of CODIGOS) {
      await db.saldosDiarios
        .where('[cuenta+fecha]')
        .between([cuenta, Dexie.minKey], [cuenta, Dexie.maxKey])
        .each((d) => {
          s[cuenta] = (s[cuenta] ?? 0) + d.debe - d.haber;
        });
    }
    return s;
  }, []);
  const saldo = (codigo: string) => centavos(libros?.[codigo] ?? 0);
  /** What each bank account holds by the books, a dollar account's dollars too: what its statement is held against. */
  const bancos = useLiveQuery(() => saldosDeBancos(CUENTAS_BANCO), []);

  const [actual, setActual] = useState('empezar');
  const ultimo = useRef(0);
  const ir = (id: string) => {
    setError(null);
    setActual(id);
  };
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The envelopes.
  /** Not in hand: left waiting, for the next going over. */
  const [noEstan, setNoEstan] = useState<Record<string, true>>({});
  const [juntos, setJuntos] = useState(VACIO_CONTEO);
  /** The total did not come out: each envelope counted on its own, to know whose it is. */
  const [cadaUno, setCadaUno] = useState(false);
  const [recibidosJuntos, setRecibidosJuntos] = useState<SesionCaja[] | null>(null);
  /**
   * What changed while the envelopes were counted together, so they are counted again: a till
   * handed over more, or one was received on another phone and the rest are counted without it.
   */
  const [recontar, setRecontar] = useState<'mas' | 'enOtro' | null>(null);
  /** Received on another phone meanwhile: still listed, with nothing to count. */
  const [enOtro, setEnOtro] = useState<Record<string, true>>({});
  const [crecio, setCrecio] = useState<Record<string, true>>({});
  const [conteos, setConteos] = useState<Record<string, { cordobas: Contando; dolares: Contando }>>({});
  const [recibidos, setRecibidos] = useState<Record<string, Recibido>>({});

  // The transfers.
  const [transferencias, setTransferencias] = useState<Record<string, Transferencia>>({});
  /** The transfers whose «No llegó» was tapped: their second question is a step of its own. */
  const [noLlegaron, setNoLlegaron] = useState<Record<string, true>>({});
  const [motivos, setMotivos] = useState<Record<string, string>>({});
  /** A reference typed for a payment that came without one, as «Transferencias» asks for it. */
  const [referencias, setReferencias] = useState<Record<string, string>>({});
  /** The córdoba account each transfer was seen in, when the shop has more than one (`cuentaTransferencias` until picked). */
  const [cuentasVistas, setCuentasVistas] = useState<Record<string, CuentaBancoCordobas>>({});

  // What left the safe, and the safe.
  const [anotando, setAnotando] = useState<Anotando | null>(null);
  /** Where a payment to a supplier was started from, to go back to once it is written down. */
  const [pagandoDesde, setPagandoDesde] = useState<'pagos' | 'proveedores'>('pagos');
  const [fuerte, setFuerte] = useState(VACIO_CONTEO);
  const [fuerteHecho, setFuerteHecho] = useState<{
    contadoC: Centavos;
    contadoD: Centavos;
    valor: Centavos;
    esperado: Centavos;
    inicial: boolean;
    primero: Centavos | null;
    /** The count, and what the books found missing at it, to be explained if it was spent. */
    eventoId: string;
    falto: Centavos;
  } | null>(null);
  /** A shortage being explained (the count's event), and a record being voided. */
  const [explicando, setExplicando] = useState<string | null>(null);
  const [anulando, setAnulando] = useState<Anulando | null>(null);

  // The statements.
  const [estados, setEstados] = useState<Partial<Record<Cuenta, string>>>({});
  /** Each statement as compared: the dollar account's in dollars, the books' and the statement's. */
  const [conciliadas, setConciliadas] = useState<
    Partial<Record<Cuenta, { esperado: Centavos; saldo: Centavos; dolares: { libros: Centavos; estado: Centavos } | null }>>
  >({});
  const [sinEstado, setSinEstado] = useState<Partial<Record<Cuenta, true>>>({});

  const [cuadrado, setCuadrado] = useState(false);

  /**
   * What these steps wrote down, listed whatever day it is dated: a deposit made before the week
   * and written down now would drop out of the week's list, and look as if it had not saved.
   */
  const [anotados, setAnotados] = useState<Record<'gastos' | 'depositos' | 'retiros' | 'pagos', string[]>>({
    gastos: [],
    depositos: [],
    retiros: [],
    pagos: [],
  });
  const anotado = (que: keyof typeof anotados, ids: string[], volverA: string) => {
    setAnotados((x) => ({ ...x, [que]: [...x[que], ...ids] }));
    setAnotando(null);
    ir(volverA);
  };
  const gasto = usePasosDeGasto({ ir, alGuardar: (id) => anotado('gastos', [id], 'gastos') });
  const deposito = usePasosDeDeposito({ ir, alGuardar: (d) => anotado('depositos', [d.id], 'depositos') });
  const dueno = usePasosDelDueno({ ir, alGuardar: (ids) => anotado('retiros', ids, 'dueno') });
  const pago = usePasosDePago({ ir, alGuardar: (id) => anotado('pagos', [id], pagandoDesde) });

  const dias = semanaPorCuadrar(inicio?.ultimo ?? null, hoy());
  /** What the week already has written down, and what is owed to suppliers. */
  const semana = useLiveQuery(
    async () => {
      const enLaSemana = (fecha: string) => fecha >= dias.desde && fecha <= dias.hasta;
      /** The week's, and what these steps wrote down dated before it, each once, by day. */
      const yLoAnotado = <T extends { id: string }>(deLaSemana: T[], anotadas: (T | undefined)[], fecha: (x: T) => string) =>
        [...deLaSemana, ...anotadas.filter((x): x is T => x !== undefined && !deLaSemana.some((y) => y.id === x.id))].sort((a, b) =>
          fecha(a).localeCompare(fecha(b)),
        );
      const fechaDePago = (e: { payload?: unknown }) => (e.payload as { fecha: string }).fecha;
      const sinEfecto = await leerSinEfecto();
      const [gastos, depositos, retiros, pagos, compras, todosLosGastos, proveedores] = await Promise.all([
        db.gastos.where('fecha').between(dias.desde, dias.hasta, true, true).filter((g) => !g.anulado).toArray(),
        // What left the safe for the bank: not what was brought back from it.
        db.depositos.where('fecha').between(dias.desde, dias.hasta, true, true).filter((d) => !d.delBanco).toArray(),
        db.movimientosDueno.filter((m) => m.tipo === 'retiro' && enLaSemana(m.fecha)).toArray(),
        db.eventos
          .where('type')
          .anyOf(['compra.pagada', 'gasto.pagado'])
          .filter((e) => tuvoEfecto(e, sinEfecto) && enLaSemana(fechaDePago(e)))
          .toArray(),
        db.compras.toArray(),
        db.gastos.toArray(),
        db.proveedores.toArray(),
      ]);
      const [masGastos, masDepositos, masRetiros, masPagos] = await Promise.all([
        db.gastos.bulkGet(anotados.gastos),
        db.depositos.bulkGet(anotados.depositos),
        db.movimientosDueno.bulkGet(anotados.retiros),
        db.eventos.bulkGet(anotados.pagos),
      ]);
      const deQuien = (proveedorId: string | null | undefined) =>
        proveedores.find((p) => p.id === proveedorId)?.nombre ?? t('gastos.nadieEnParticular');
      /**
       * The parts paid with a purchase in the week (`conLaCompra`): money that left as a payment does,
       * listed so the trucker's cash is not written down a second time against what is still owed.
       * Voided on its own only while the purchase's rest is owed, as the books take it.
       */
      const partes: {
        id: string;
        monto: Centavos;
        fecha: string;
        nombre: string;
        deuda: { tipo: 'compra' | 'gasto'; id: string };
        pago: PagoProveedor | undefined;
        conLaCompra: boolean;
        conElGasto: boolean;
      }[] = [
        ...compras
          .filter((c) => !c.anulada && enLaSemana(c.fecha))
          .flatMap((c) =>
            (c.pagos ?? [])
              .filter((x) => x.conLaCompra && !x.anulado)
              .map((x) => ({
                id: `${c.id}:${x.id}`,
                monto: x.monto,
                fecha: x.fecha,
                nombre: deQuien(c.proveedorId),
                deuda: { tipo: 'compra' as const, id: c.id },
                pago: c.formaPago === 'credito' ? x : undefined,
                conLaCompra: true,
                conElGasto: false,
              })),
          ),
        /*
         * And those of an expense bought on credit (2026-10-16), paid against what it owed: a later
         * payment written down here would pay them again. One paid in full is among the expenses.
         */
        ...todosLosGastos
          .filter((g) => !g.anulado && g.formaPago === 'credito' && enLaSemana(g.fecha))
          .flatMap((g) =>
            (g.pagos ?? [])
              .filter((x) => x.conElGasto && !x.anulado)
              .map((x) => ({
                id: `${g.id}:${x.id}`,
                monto: x.monto,
                fecha: x.fecha,
                nombre: deQuien(g.proveedorId),
                deuda: { tipo: 'gasto' as const, id: g.id },
                pago: x,
                conLaCompra: false,
                conElGasto: true,
              })),
          ),
      ];
      // A voided one is not on the list: it did not happen.
      return {
        gastos: yLoAnotado(gastos, masGastos.filter((g) => !g?.anulado), (g) => g.fecha),
        depositos: yLoAnotado(depositos.filter((d) => !d.anulado), masDepositos.filter((d) => !d?.anulado), (d) => d.fecha),
        retiros: yLoAnotado(retiros.filter((m) => !m.anulado), masRetiros.filter((m) => !m?.anulado), (m) => m.fecha),
        pagos: yLoAnotado(pagos, masPagos.filter((e) => e !== undefined && tuvoEfecto(e, sinEfecto)), fechaDePago).flatMap((e) => {
          const p = e.payload as { compraId?: string; gastoId?: string; pagoId?: string; monto: number; fecha: string };
          const deuda = p.compraId ? compras.find((c) => c.id === p.compraId) : todosLosGastos.find((g) => g.id === p.gastoId);
          // As the debt keeps it, if it named itself (`pagoId`): only then can it be voided. One the
          // debt does not keep, the books refused, though the log may say it was taken (`pagoDeLaDeuda`).
          const pago = pagoDeLaDeuda(p.pagoId, deuda);
          if (pago === null || pago?.anulado) return [];
          return [
            {
              id: e.id,
              monto: centavos(p.monto),
              fecha: p.fecha,
              nombre: deQuien(deuda?.proveedorId),
              deuda: p.compraId ? { tipo: 'compra' as const, id: p.compraId } : { tipo: 'gasto' as const, id: p.gastoId ?? '' },
              pago,
              conLaCompra: false,
              conElGasto: false,
            },
          ];
        })
          .concat(partes)
          .sort((a, b) => a.fecha.localeCompare(b.fecha)),
        deudas: porPagar(compras, todosLosGastos).length,
        pronto: porPagarPronto(compras, todosLosGastos, dias.hasta).map((d) => ({ ...d, nombre: deQuien(d.proveedorId) })),
      };
    },
    [dias.desde, dias.hasta, anotados],
  );

  const titulo = t('cuadrar.titulo');
  if (!puede) {
    return (
      <Pagina titulo={titulo} volverA="/">
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }
  if (!inicio) return <Pagina titulo={titulo}>{null}</Pagina>;

  const tasa = fxRate(config.fxTasaOficial);
  const nombre = (id: string) => usuarios.find((u) => u.id === id)?.nombre ?? t('ventas.alguien');
  const deQuien = (s: SesionCaja) => ({
    nombre: nombre(responsableDe(s)),
    fecha: fechaCorta(fechaEnNicaragua(s.cerradaEn ?? s.abiertaEn)),
  });
  const conteoDe = (id: string) => conteos[id] ?? VACIO_CONTEO;
  const ponerConteo = (id: string, moneda: 'cordobas' | 'dolares', c: Contando) =>
    setConteos((x) => ({ ...x, [id]: { ...(x[id] ?? VACIO_CONTEO), [moneda]: c } }));

  const sobres = inicio.cajas;
  const elegidos = sobres.filter((s) => !noEstan[s.id] && !enOtro[s.id]);
  const total = totalDeSobres(elegidos);
  /**
   * Whether the suppliers' payments are asked about: something is owed, or was paid this week, or
   * the question is on screen or its steps are open. A payment of the only debt, dated before the
   * week, would otherwise take its own question away, and the steps after it with it.
   */
  const pagandoAqui = (desde: 'pagos' | 'proveedores') => actual === desde || (anotando === 'pago' && pagandoDesde === desde);
  const conPagos = (semana?.deudas ?? 0) > 0 || (semana?.pagos.length ?? 0) > 0 || pagandoAqui('pagos');

  // --- Receiving -------------------------------------------------------------------------------

  /**
   * The envelopes counted together, received if the total is what the tills handed over; if it is
   * not, each is counted on its own, so what is short is the right till's. A till that handed over
   * more since the steps began is counted again, all of it.
   */
  async function recibirJuntos() {
    const cC = contado(juntos.cordobas, 'NIO');
    const cD = contado(juntos.dolares, 'USD');
    if (cC === null || cD === null || trabajando) return;
    setTrabajando(true);
    setError(null);
    try {
      const vivas = await Promise.all(elegidos.map((s) => db.sesiones.get(s.id)));
      const cambio = vivas.some(
        (v, i) =>
          (v?.porRecibir ?? 0) !== (elegidos[i]!.porRecibir ?? 0) ||
          (v?.porRecibirDolares ?? 0) !== (elegidos[i]!.porRecibirDolares ?? 0),
      );
      if (cambio) {
        const nuevas = new Map(vivas.filter((v): v is SesionCaja => Boolean(v)).map((v) => [v.id, v]));
        // Received on another phone: listed as such, and the rest counted without it.
        const fuera = elegidos.filter((_, i) => !vivas[i] || !conEfectivo(vivas[i]!)).map((s) => s.id);
        setEnOtro((x) => ({ ...x, ...Object.fromEntries(fuera.map((id) => [id, true as const])) }));
        setInicio((x) => x && { ...x, cajas: x.cajas.map((y) => (fuera.includes(y.id) ? y : (nuevas.get(y.id) ?? y))) });
        setJuntos(VACIO_CONTEO);
        if (fuera.length === elegidos.length) {
          // None left to count: the list says why, and the way on is past them.
          setRecontar(null);
          setActual('sobres');
          return;
        }
        setRecontar(fuera.length > 0 ? 'enOtro' : 'mas');
        setActual('sobres:cordobas');
        return;
      }
      if (cC !== total.cordobas || cD !== total.dolares) {
        setCadaUno(true);
        setActual('sobres:cada');
        return;
      }
      for (const s of elegidos) {
        const { rechazo } = await emitirYVer('efectivo.recibido', {
          sesionId: s.id,
          contadoCordobas: s.porRecibir ?? 0,
          contadoDolares: s.porRecibirDolares ?? 0,
          nota: 'Contado junto con los otros sobres: el total cuadró',
        });
        if (rechazo) {
          setError(rechazo);
          return;
        }
      }
      setRecibidosJuntos(elegidos);
      setActual('sobres:resultado');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  /** One envelope, counted on its own. */
  async function recibir(s: SesionCaja) {
    const c = conteoDe(s.id);
    const cC = contado(c.cordobas, 'NIO');
    const cD = contado(c.dolares, 'USD');
    if (cC === null || cD === null) return;
    const [primeroC, primeroD] = [primerConteo(c.cordobas, cC), primerConteo(c.dolares, cD)];
    setTrabajando(true);
    setError(null);
    try {
      /*
       * What the till holds now, not when the steps began: a retiro or the corte made meanwhile
       * hands over more, and the books hold the count against all of it. Received as counted, the
       * first envelope would come up short by the second, on the cashier's name.
       */
      const viva = await db.sesiones.get(s.id);
      const [ahoraC, ahoraD] = [viva?.porRecibir ?? 0, viva?.porRecibirDolares ?? 0];
      if (!viva || (ahoraC === 0 && ahoraD === 0)) {
        // Received on another phone meanwhile: nothing is left to receive here, nor to hold against it.
        const nada = centavos(0);
        setRecibidos((x) => ({
          ...x,
          [s.id]: { esperadoC: nada, contadoC: nada, esperadoD: nada, contadoD: nada, primeroC: null, primeroD: null, enOtro: true },
        }));
        setActual(`recibir:${s.id}:resultado`);
        return;
      }
      if (ahoraC !== (s.porRecibir ?? 0) || ahoraD !== (s.porRecibirDolares ?? 0)) {
        setInicio((x) => x && { ...x, cajas: x.cajas.map((y) => (y.id === s.id ? viva : y)) });
        setConteos(({ [s.id]: _, ...resto }) => resto);
        setCrecio((x) => ({ ...x, [s.id]: true }));
        setActual(`recibir:${s.id}:cordobas`);
        return;
      }
      const { id: eventoId, rechazo } = await emitirYVer('efectivo.recibido', {
        sesionId: s.id,
        contadoCordobas: cC,
        contadoDolares: cD,
        ...(primeroC !== null ? { primerConteoCordobas: primeroC } : {}),
        ...(primeroD !== null ? { primerConteoDolares: primeroD } : {}),
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      // Both currencies together, as the books hold them: short in one and over in the other nets.
      const { falto } = await faltanteDelConteo(eventoId);
      setRecibidos((x) => ({
        ...x,
        [s.id]: {
          esperadoC: (s.porRecibir ?? 0) as Centavos,
          contadoC: cC,
          esperadoD: (s.porRecibirDolares ?? 0) as Centavos,
          contadoD: cD,
          primeroC,
          primeroD,
          eventoId,
          falto,
        },
      }));
      setActual(`recibir:${s.id}:resultado`);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  // --- The safe -------------------------------------------------------------------------------

  /**
   * Never counted before: this count is where the safe starts. As it stands now, not as it stood
   * when the steps began: counted meanwhile on another phone, this count is what it finds over or
   * short, as the books will take it whatever this phone says (`cuentasContadas`).
   */
  const inicial = (ahora ?? inicio).primerArqueo && !yaContada(config, 'CAJA_FUERTE');
  const fC = contado(fuerte.cordobas, 'NIO');
  const fD = contado(fuerte.dolares, 'USD');

  /** Held against the books once, both currencies at the official rate; the first count never is. */
  function revisarFuerte() {
    if (fC === null || fD === null) return;
    const valor = centavos(fC + usdToCordobas(fD, tasa));
    const esperado = saldo(ACCOUNTS.CAJA_FUERTE.code);
    if (!inicial && !fuerte.cordobas.otraVez && pideReconteo(valor, esperado, 'NIO', config.cajaReconteoDesde)) {
      setFuerte({
        cordobas: { ...SIN_CONTAR, primero: fC, otraVez: true },
        dolares: { ...SIN_CONTAR, primero: fD, otraVez: true },
      });
      setActual('fuerte:cordobas');
      return;
    }
    void contarFuerte(valor, esperado);
  }

  async function contarFuerte(valor: Centavos, esperado: Centavos) {
    if (trabajando || fC === null || fD === null) return;
    const primeroC = fuerte.cordobas.otraVez ? fuerte.cordobas.primero : null;
    const primeroD = fuerte.dolares.otraVez ? fuerte.dolares.primero : null;
    setTrabajando(true);
    setError(null);
    try {
      const { id: eventoId, rechazo } = await emitirYVer('efectivo.arqueo', {
        contadoCordobas: fC,
        contadoDolares: fD,
        esperado,
        ...(inicial ? { inicial: true } : {}),
        ...(primeroC !== null ? { primerConteoCordobas: primeroC } : {}),
        ...(primeroD !== null ? { primerConteoDolares: primeroD } : {}),
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setFuerteHecho({
        contadoC: fC,
        contadoD: fD,
        valor,
        esperado,
        inicial,
        primero: primeroC !== null ? centavos(primeroC + usdToCordobas(centavos(primeroD ?? 0), tasa)) : null,
        eventoId,
        // As the books took it: the first count's difference is where the safe starts, not missing.
        falto: (await faltanteDelConteo(eventoId)).falto,
      });
      setActual('fuerte:resultado');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  // --- The transfers and the statements ---------------------------------------------------------

  const destino = destinoDe;
  const referenciaDe = (x: EnCola) => x.referencia ?? referencias[x.clave]?.trim() ?? '';
  /** The account a transfer was seen in: the one picked, while still open, or where transfers go. */
  const cuentaVista = (x: EnCola): CuentaBancoCordobas => cuentaEnCordobas(config, cuentasVistas[x.clave]);

  async function verTransferencia(x: EnCola, que: Transferencia, siguienteId: string) {
    if (trabajando) return;
    setError(null);
    // Seen to another way after «No llegó» and «Atrás»: its «¿Qué pasó?» goes.
    const sinNoLlego = () => setNoLlegaron(({ [x.clave]: _, ...resto }) => resto);
    if (que === 'espera') {
      sinNoLlego();
      setTransferencias((v) => ({ ...v, [x.clave]: que }));
      setActual(siguienteId);
      return;
    }
    setTrabajando(true);
    try {
      const { rechazo } =
        que === 'llego'
          ? await emitirYVer('pago.confirmado', {
              ...destino(x),
              referencia: referenciaDe(x),
              ...(x.forma === 'billetera' ? {} : { cuenta: cuentaVista(x) }),
            })
          : await emitirYVer('pago.rechazado', {
              ...destino(x),
              motivo: (motivos[x.clave] ?? '').trim(),
              ...(que === 'fiado' ? { aFiado: true } : {}),
            });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      if (que === 'llego') sinNoLlego();
      setTransferencias((v) => ({ ...v, [x.clave]: que }));
      setActual(siguienteId);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  /**
   * The statement's balance against the books on the same day; the difference, if any, booked. The
   * dollar account's in dollars, and only its dollars over or under go on at the official rate
   * (`saldoDelEstadoEnDolares`).
   */
  async function conciliar(c: Cuenta, monto: Centavos) {
    if (trabajando) return;
    const esperado = saldo(ACCOUNTS[c].code);
    const dolares = c !== 'BILLETERA_MOVIL' && esCuentaEnDolares(c) ? { libros: centavos(bancos?.[c]?.dolares ?? 0), estado: monto } : null;
    const valor = dolares
      ? saldoDelEstadoEnDolares({ libros: esperado, librosDolares: dolares.libros, estadoDolares: monto, tasa })
      : monto;
    setTrabajando(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('cuenta.conciliada', {
        cuenta: c,
        fecha: hoy(),
        saldo: valor,
        ...(dolares ? { saldoDolares: monto } : {}),
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setConciliadas((x) => ({ ...x, [c]: { esperado, saldo: valor, dolares } }));
      setActual(`estado:${c}:resultado`);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  async function terminar() {
    setTrabajando(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('semana.cuadrada', { desde: dias.desde, hasta: dias.hasta });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setCuadrado(true);
      setActual('listo');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  // --- The steps --------------------------------------------------------------------------------

  const siguienteDe = (id: string) => {
    const lista = pasos().map((p) => p.id);
    return lista[lista.indexOf(id) + 1] ?? id;
  };

  const abiertas = (ahora ?? inicio).abiertas;
  /** What is left for the owner once the week is gone over: each a way to where it is seen to. */
  const quedan: Aviso[] = [
    ...abiertas.map((s) => ({
      clave: `abierta:${s.id}`,
      texto: t('cuadrar.sigueAbierta', { nombre: nombre(responsableDe(s)) }),
      icono: 'caja' as const,
      tinte: 'ambar' as const,
      // Its cash is received here once it is handed over.
      a: '/efectivo' as const,
    })),
    ...(pendientes ?? []),
  ];

  const aviso = (texto: string) => (
    <p className="rounded-xl bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-200">{texto}</p>
  );
  type Fila = { clave: string; texto: string; detalle?: string; monto: string; anular?: () => void };
  const lista = (filas: Fila[], vacio: string, marca: string) =>
    filas.length === 0 ? (
      <p className="tarjeta p-3 text-sm text-slate-400">{vacio}</p>
    ) : (
      <ul className="tarjeta flex flex-col gap-2" {...{ [marca]: '' }}>
        {filas.map((f) => (
          <li key={f.clave} className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 wrap-break-word">
              {f.texto}
              {f.detalle && <span className="block text-xs text-slate-500">{f.detalle}</span>}
              {/* Under the line, as «Anular» sits under an abono: beside the amount it squeezed the name. */}
              {f.anular && (
                <button type="button" className="btn-texto mt-0.5 gap-1 text-xs text-slate-500" onClick={f.anular} data-cuadrar-anular>
                  <Ico icono="anular" className="size-3.5" />
                  {t('comun.anular')}
                </button>
              )}
            </span>
            <span className="tabular shrink-0 font-medium">{f.monto}</span>
          </li>
        ))}
      </ul>
    );

  function pasoDeSobres(): Paso {
    return {
      id: 'sobres',
      titulo: t('sobres.titulo'),
      explicacion: <p>{t('sobres.explicacion')}</p>,
      contenido: (
        <ul className="flex flex-col gap-2" data-cuadrar-sobres>
          {sobres.map((s) => {
            const { nombre: quien, fecha } = deQuien(s);
            const yaNo = Boolean(enOtro[s.id]);
            const esta = !noEstan[s.id] && !yaNo;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  aria-pressed={esta}
                  disabled={yaNo}
                  className={`tarjeta-accion flex w-full items-center gap-3 p-3.5 text-left disabled:opacity-60 ${esta ? 'ring-2 ring-marca-400' : ''}`}
                  onClick={() => {
                    setNoEstan(({ [s.id]: ya, ...resto }) => (ya ? resto : { ...resto, [s.id]: true }));
                    // Another set of envelopes is another total: counted from the start.
                    setJuntos(VACIO_CONTEO);
                  }}
                >
                  <Ico icono={esta || yaNo ? 'listo' : 'despues'} className="size-6 shrink-0 text-marca-300" />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{t('sobres.de', { nombre: quien, fecha })}</span>
                    <span className="block text-sm text-slate-400">
                      {t(yaNo ? 'sobres.yaRecibido' : esta ? 'sobres.loTengo' : 'sobres.noLoTengo')}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ),
      seguir:
        elegidos.length > 0
          ? {
              etiqueta: t('sobres.contar', { n: elegidos.length }),
              icono: 'contar',
              alTocar: () => ir(siguienteDe('sobres')),
            }
          : { etiqueta: t('sobres.ninguno'), alTocar: () => ir('gastos') },
    };
  }

  function pasosJuntos(): Paso[] {
    const n = elegidos.length;
    const cC = contado(juntos.cordobas, 'NIO');
    const cD = contado(juntos.dolares, 'USD');
    const cordobas = pasoDeConteo({
      id: 'sobres:cordobas',
      moneda: 'NIO',
      contando: juntos.cordobas,
      poner: (x) => setJuntos((j) => ({ ...j, cordobas: x })),
      esperado: total.cordobas,
      umbral: config.cajaReconteoDesde,
      titulo: t('sobres.contarCordobas', { n }),
      explicacion: t('sobres.contarCordobasExplicacion'),
      siguiente: () => ir('sobres:dolares'),
      t,
    });
    return [
      {
        ...cordobas,
        contenido: (
          <div className="flex flex-col gap-4">
            {recontar && aviso(t(recontar === 'mas' ? 'sobres.entregaronMas' : 'sobres.recibidoEnOtro'))}
            {cordobas.contenido}
          </div>
        ),
      },
      pasoDeConteo({
        id: 'sobres:dolares',
        moneda: 'USD',
        contando: juntos.dolares,
        poner: (x) => setJuntos((j) => ({ ...j, dolares: x })),
        esperado: total.dolares,
        umbral: config.cajaReconteoDesde,
        titulo: t('sobres.contarDolares'),
        explicacion: t('cuadrar.contarDolaresExplicacion'),
        siguiente: () => ir('sobres:revise'),
        t,
      }),
      {
        id: 'sobres:revise',
        titulo: t('turno.revise'),
        explicacion: <p>{t('sobres.revise', { n })}</p>,
        contenido: (
          <div className="flex flex-col gap-4">
            <div className="tarjeta flex flex-col gap-2">
              <Dato etiqueta={t('turno.contoCordobas')} valor={formatMoney(cC ?? (0 as Centavos))} />
              <Dato etiqueta={t('turno.contoDolares')} valor={enDolares(cD ?? (0 as Centavos))} />
            </div>
            {error && <p className="text-sm text-rose-400">{error}</p>}
          </div>
        ),
        seguir: {
          etiqueta: t('cuadrar.recibir'),
          icono: 'recibir',
          deshabilitado: trabajando || cC === null || cD === null || recibidosJuntos !== null,
          alTocar: () => void recibirJuntos(),
        },
      },
      {
        id: 'sobres:resultado',
        titulo: t('sobres.cuadranTitulo', { n: recibidosJuntos?.length ?? n }),
        sinAtras: true,
        explicacion: recibidosJuntos && <p>{t('sobres.cuadranExplicacion')}</p>,
        contenido: recibidosJuntos ? (
          <div className="flex flex-col gap-4" data-cuadrar-recibido>
            {lista(
              recibidosJuntos.map((s) => {
                const { nombre: quien, fecha } = deQuien(s);
                return {
                  clave: s.id,
                  texto: t('sobres.de', { nombre: quien, fecha }),
                  monto: [
                    formatMoney(centavos(s.porRecibir ?? 0)),
                    ...((s.porRecibirDolares ?? 0) > 0 ? [enDolares(centavos(s.porRecibirDolares ?? 0))] : []),
                  ].join(' · '),
                };
              }),
              '',
              'data-cuadrar-sobres-recibidos',
            )}
            {juntos.cordobas.primero !== null && juntos.cordobas.otraVez && (
              <Dato etiqueta={t('turno.primerConteo')} valor={formatMoney(juntos.cordobas.primero)} />
            )}
          </div>
        ) : null,
        seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => ir(siguienteDe('sobres:resultado')) },
      },
    ];
  }

  function pasosDeCaja(s: SesionCaja): Paso[] {
    const c = conteoDe(s.id);
    const hecho = recibidos[s.id];
    const { nombre: quien, fecha } = deQuien(s);
    const cC = contado(c.cordobas, 'NIO');
    const cD = contado(c.dolares, 'USD');
    const id = (parte: string) => `recibir:${s.id}:${parte}`;
    const cordobas = pasoDeConteo({
      id: id('cordobas'),
      moneda: 'NIO',
      contando: c.cordobas,
      poner: (x) => ponerConteo(s.id, 'cordobas', x),
      esperado: (s.porRecibir ?? 0) as Centavos,
      umbral: config.cajaReconteoDesde,
      titulo: t('cuadrar.contarCordobas', { nombre: quien, fecha }),
      explicacion: t('cuadrar.contarCordobasExplicacion', { nombre: quien }),
      siguiente: () => ir(id('dolares')),
      t,
    });
    return [
      {
        ...cordobas,
        contenido: (
          <div className="flex flex-col gap-4">
            {crecio[s.id] && aviso(t('cuadrar.entregoMas', { nombre: quien }))}
            {cordobas.contenido}
          </div>
        ),
      },
      pasoDeConteo({
        id: id('dolares'),
        moneda: 'USD',
        contando: c.dolares,
        poner: (x) => ponerConteo(s.id, 'dolares', x),
        esperado: (s.porRecibirDolares ?? 0) as Centavos,
        umbral: config.cajaReconteoDesde,
        titulo: t('cuadrar.contarDolares', { nombre: quien }),
        explicacion: t('cuadrar.contarDolaresExplicacion'),
        siguiente: () => ir(id('revise')),
        t,
      }),
      {
        id: id('revise'),
        titulo: t('turno.revise'),
        explicacion: <p>{t('cuadrar.reviseRecibir', { nombre: quien })}</p>,
        contenido: (
          <div className="flex flex-col gap-4">
            <div className="tarjeta flex flex-col gap-2">
              <Dato etiqueta={t('turno.contoCordobas')} valor={formatMoney(cC ?? (0 as Centavos))} />
              <Dato etiqueta={t('turno.contoDolares')} valor={enDolares(cD ?? (0 as Centavos))} />
            </div>
            {error && <p className="text-sm text-rose-400">{error}</p>}
          </div>
        ),
        seguir: {
          etiqueta: t('cuadrar.recibir'),
          icono: 'recibir',
          deshabilitado: trabajando || cC === null || cD === null || Boolean(hecho),
          alTocar: () => void recibir(s),
        },
      },
      {
        id: id('resultado'),
        titulo: t(hecho?.enOtro ? 'cuadrar.yaRecibidoTitulo' : 'cuadrar.recibidoTitulo', { nombre: quien }),
        sinAtras: true,
        explicacion: hecho && <p>{t(hecho.enOtro ? 'cuadrar.yaRecibidoExplicacion' : 'cuadrar.recibidoExplicacion', { nombre: quien })}</p>,
        contenido: hecho && !hecho.enOtro ? (
          <div className="flex flex-col gap-5" data-cuadrar-recibido>
            <Veredicto
              esperado={t('cuadrar.entregoCordobas')}
              valorEsperado={formatMoney(hecho.esperadoC)}
              valorContado={formatMoney(hecho.contadoC)}
              diferencia={(hecho.contadoC - hecho.esperadoC) as Centavos}
              cuadra={t(hecho.esperadoD > 0 || hecho.contadoD > 0 ? 'caja.cordobasCuadran' : 'caja.cuadraExacto')}
              formato={(x) => formatMoney(x)}
              primerConteo={hecho.primeroC !== null ? formatMoney(hecho.primeroC) : null}
            />
            {(hecho.esperadoD > 0 || hecho.contadoD > 0) && (
              <Veredicto
                esperado={t('cuadrar.entregoDolares')}
                valorEsperado={enDolares(hecho.esperadoD)}
                valorContado={enDolares(hecho.contadoD)}
                diferencia={(hecho.contadoD - hecho.esperadoD) as Centavos}
                cuadra={t('caja.dolaresCuadran')}
                formato={(x) => enDolares(x)}
                primerConteo={hecho.primeroD !== null ? enDolares(hecho.primeroD) : null}
              />
            )}
            {/* Short as the books net it: córdobas short and dollars over by as much is not short (2026-10-13). */}
            {(hecho.falto !== undefined ? 0 < hecho.falto : hecho.esperadoC > hecho.contadoC || hecho.esperadoD > hecho.contadoD) && (
              <p className="text-sm text-slate-400">{t('cuadrar.faltanteDe', { nombre: quien })}</p>
            )}
            {hecho.eventoId && (hecho.falto ?? 0) > 0 && (
              <button type="button" className="btn-secundario" onClick={() => setExplicando(hecho.eventoId!)} data-cuadrar-explicar>
                <Ico icono="gastos" />
                {t('explicar.botonFaltante')}
              </button>
            )}
          </div>
        ) : null,
        seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => ir(siguienteDe(id('resultado'))) },
      },
    ];
  }

  /** One of the four things that leave the safe: what the week has, and «¿falta algo?». */
  function pasoQueSalio(x: {
    id: 'gastos' | 'depositos' | 'dueno' | 'pagos';
    titulo: string;
    explicacion: string;
    filas: Fila[];
    vacio: string;
    anotar: string;
    anotarDetalle: string;
    nada: string;
    abrir: () => void;
  }): Paso {
    return {
      id: x.id,
      titulo: x.titulo,
      explicacion: <p>{x.explicacion}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          {lista(x.filas, x.vacio, `data-cuadrar-${x.id}`)}
          <Respuestas
            opciones={[
              { etiqueta: x.anotar, detalle: x.anotarDetalle, icono: 'nuevo', alTocar: x.abrir },
              {
                etiqueta: x.nada,
                icono: 'adelante',
                alTocar: () => {
                  setAnotando(null);
                  // On past this question's own steps, if they were started and left.
                  const ids = pasos().map((p) => p.id);
                  const despues = ids.slice(ids.indexOf(x.id) + 1).find((i) => !/^(gasto|deposito|dueno|pago)-/.test(i));
                  ir(despues ?? x.id);
                },
              },
            ]}
          />
        </div>
      ),
    };
  }

  function pasosDeTransferencia(x: EnCola): Paso[] {
    const visto = transferencias[x.clave];
    const cliente = x.venta?.clienteId ? clientes.find((c) => c.id === x.venta!.clienteId) : undefined;
    const enBilletera = x.forma === 'billetera';
    const id = `transferencia:${x.clave}`;
    const idNo = `${id}:no`;
    const referencia = referenciaDe(x);
    const repetida = otrosUsos(usos, referencia, x.clave)[0];
    const espera = diasDesde(x.fecha);
    const despues = () => siguienteDe(noLlegaron[x.clave] ? idNo : id);
    const tarjeta = (
      <div className="tarjeta flex flex-col gap-2">
        <Dato etiqueta={t('cuadrar.monto')} valor={formatMoney(x.monto)} />
        <DatoTexto
          etiqueta={t(x.venta ? 'cuadrar.deLaVenta' : 'cuadrar.deLaRecarga')}
          valor={x.venta ? x.venta.ticket : t('transf.recarga', { operadora: x.recarga!.operadora, telefono: x.recarga!.telefono })}
        />
        {cliente && <DatoTexto etiqueta={t('cuadrar.cliente')} valor={cliente.nombre} />}
        <DatoTexto etiqueta={t('comun.referencia')} valor={x.referencia ?? t('cuadrar.sinReferencia')} />
        <Dato etiqueta={t('cuadrar.fecha')} valor={fechaCorta(x.fecha)} />
        {/* A day or more without arriving is a transfer that may never come: said in its colour. */}
        {espera > 0 && (
          <p className={`text-sm ${espera >= 2 ? 'text-rose-300' : 'text-amber-300'}`}>
            {espera === 1 ? t('transf.deAyer') : t('transf.haceDias', { n: espera })}
          </p>
        )}
        {repetida && (
          <p className="text-sm font-medium text-rose-300">
            {t(yaPago(repetida, true).clave, yaPago(repetida, true).valores)}
          </p>
        )}
      </div>
    );
    const yaVisto = visto === 'llego' || visto === 'fiado' || visto === 'perdida';
    const pregunta: Paso = {
      id,
      titulo: t(enBilletera ? 'cuadrar.billeteraTitulo' : 'cuadrar.transferenciaTitulo'),
      explicacion: <p>{t(enBilletera ? 'cuadrar.billeteraExplicacion' : 'cuadrar.transferenciaExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          {tarjeta}
          {yaVisto ? (
            <p className="rounded-xl bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-300">
              {t(visto === 'llego' ? 'cuadrar.confirmada' : visto === 'fiado' ? 'cuadrar.alFiado' : 'cuadrar.perdida', {
                nombre: cliente?.nombre ?? '',
              })}
            </p>
          ) : (
            <>
              {!x.referencia && (
                <Campo
                  maxLength={80}
                  etiqueta={t('transf.referenciaAlConfirmar')}
                  value={referencias[x.clave] ?? ''}
                  onChange={(e) => setReferencias((r) => ({ ...r, [x.clave]: e.target.value }))}
                  autoComplete="off"
                  ayuda={t('cuadrar.referenciaAyuda')}
                />
              )}
              {!enBilletera && (
                <ElegirCuenta
                  valor={cuentaVista(x)}
                  onCambiar={(c) => setCuentasVistas((v) => ({ ...v, [x.clave]: c as CuentaBancoCordobas }))}
                  etiqueta={t('transf.enQueCuenta')}
                />
              )}
              <Respuestas
                deshabilitado={trabajando}
                opciones={[
                  {
                    etiqueta: t(enBilletera ? 'cuadrar.siLlegoBilletera' : 'cuadrar.siLlego'),
                    detalle: t('cuadrar.siLlegoDetalle'),
                    icono: 'confirmar',
                    alTocar: () => {
                      if (!referencia) {
                        setError(t('cuadrar.faltaReferencia'));
                        return;
                      }
                      void verTransferencia(x, 'llego', despues());
                    },
                  },
                  {
                    etiqueta: t('cuadrar.todaviaNo'),
                    detalle: t('cuadrar.todaviaNoDetalle'),
                    icono: 'despues',
                    elegida: visto === 'espera',
                    alTocar: () => void verTransferencia(x, 'espera', despues()),
                  },
                  {
                    etiqueta: t('transf.noLlego'),
                    detalle: t('cuadrar.noLlegoDetalle'),
                    icono: 'aviso',
                    alTocar: () => {
                      setNoLlegaron((v) => ({ ...v, [x.clave]: true }));
                      ir(idNo);
                    },
                  },
                ]}
              />
              {error && actual === id && <p className="text-sm text-rose-400">{error}</p>}
            </>
          )}
        </div>
      ),
      ...(yaVisto ? { seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => ir(despues()) } } : {}),
    };
    if (!noLlegaron[x.clave]) return [pregunta];
    const motivo = (motivos[x.clave] ?? '').trim();
    const no: Paso = {
      id: idNo,
      titulo: t('transf.quePaso'),
      explicacion: <p>{t(cliente ? 'cuadrar.noLlegoCliente' : 'cuadrar.noLlegoSinCliente', { nombre: cliente?.nombre ?? '' })}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          {tarjeta}
          {yaVisto ? (
            <p className="rounded-xl bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-200">
              {t(visto === 'fiado' ? 'cuadrar.alFiado' : visto === 'perdida' ? 'cuadrar.perdida' : 'cuadrar.confirmada', {
                nombre: cliente?.nombre ?? '',
              })}
            </p>
          ) : (
            <>
              <Campo
                maxLength={200}
                etiqueta={t('transf.quePaso')}
                value={motivos[x.clave] ?? ''}
                onChange={(e) => setMotivos((m) => ({ ...m, [x.clave]: e.target.value }))}
                placeholder={t('transf.ejemplo')}
                autoComplete="off"
              />
              <Respuestas
                deshabilitado={trabajando}
                opciones={[
                  ...(cliente
                    ? [
                        {
                          etiqueta: t('transf.pasarAFiado', { nombre: cliente.nombre }),
                          detalle: t('cuadrar.aFiadoDetalle'),
                          icono: 'fiado' as const,
                          alTocar: () => {
                            if (!motivo) {
                              setError(t('cuadrar.faltaMotivo'));
                              return;
                            }
                            void verTransferencia(x, 'fiado', siguienteDe(idNo));
                          },
                        },
                      ]
                    : []),
                  {
                    etiqueta: t('transf.registrarPerdida'),
                    detalle: t('cuadrar.perdidaDetalle'),
                    icono: 'desactivar',
                    alTocar: () => {
                      if (!motivo) {
                        setError(t('cuadrar.faltaMotivo'));
                        return;
                      }
                      void verTransferencia(x, 'perdida', siguienteDe(idNo));
                    },
                  },
                ]}
              />
              {error && actual === idNo && <p className="text-sm text-rose-400">{error}</p>}
            </>
          )}
        </div>
      ),
      ...(yaVisto ? { seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => ir(siguienteDe(idNo)) } } : {}),
    };
    return [pregunta, no];
  }

  function pasosDeEstado(c: Cuenta): Paso[] {
    const id = `estado:${c}`;
    const enDolar = c !== 'BILLETERA_MOVIL' && esCuentaEnDolares(c);
    const nombrada = c === 'BILLETERA_MOVIL' ? null : nombrePuesto(config, c);
    const texto = estados[c] ?? '';
    const monto = parseMoney(texto);
    const hecho = conciliadas[c];
    const formato = (x: Centavos) => (enDolar ? enDolares(x) : formatMoney(x));
    const pregunta: Paso = {
      id,
      titulo: nombrada
        ? t('estado.cuentaNombrada', { cuenta: nombrada })
        : t(c === 'BILLETERA_MOVIL' ? 'estado.billetera' : enDolar ? 'estado.dolares' : 'estado.banco'),
      explicacion: <p>{t(c === 'BILLETERA_MOVIL' ? 'estado.billeteraExplicacion' : 'estado.bancoExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <Campo
            etiqueta={t(enDolar ? 'estado.saldoDolares' : 'estado.saldoCordobas')}
            inputMode="decimal"
            className="tabular text-lg"
            value={texto}
            onChange={(e) => setEstados((x) => ({ ...x, [c]: e.target.value }))}
            placeholder="0.00"
            autoComplete="off"
            readOnly={Boolean(hecho)}
            error={texto.trim() !== '' && monto === null ? t('caja.montoInvalido') : null}
          />
          <button
            type="button"
            className="btn-secundario"
            onClick={() => {
              setSinEstado((x) => ({ ...x, [c]: true }));
              ir(siguienteDe(`${id}:resultado`));
            }}
          >
            <Ico icono="despues" />
            {t('estado.sinEstado')}
          </button>
          {error && actual === id && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      ),
      seguir: {
        etiqueta: t('estado.comparar'),
        icono: 'calcular',
        deshabilitado:
          trabajando || monto === null || texto.trim() === '' || Boolean(hecho) || libros === undefined || (enDolar && bancos === undefined),
        alTocar: () => monto !== null && void conciliar(c, monto),
      },
    };
    if (sinEstado[c] && !hecho) return [pregunta];
    // The dollar account is told in dollars, though the books carry it in córdobas.
    const [segunEstado, segunLibros] = hecho?.dolares ? [hecho.dolares.estado, hecho.dolares.libros] : [hecho?.saldo, hecho?.esperado];
    const diferencia = centavos((segunEstado ?? 0) - (segunLibros ?? 0));
    const resultado: Paso = {
      id: `${id}:resultado`,
      titulo: t('estado.resultadoTitulo'),
      sinAtras: true,
      contenido: hecho ? (
        <div className="flex flex-col gap-3" data-cuadrar-estado>
          <div className="tarjeta flex flex-col gap-2">
            <Dato etiqueta={t('estado.segunEstado')} valor={formato(centavos(segunEstado ?? 0))} />
            <Dato etiqueta={t('estado.segunLibros')} valor={formato(centavos(segunLibros ?? 0))} />
          </div>
          <div
            className={`rounded-xl px-4 py-3 ${diferencia === 0 ? 'bg-emerald-500/10 text-emerald-300' : 'bg-amber-500/10 text-amber-200'}`}
          >
            <p className="text-sm font-medium">
              {diferencia === 0 ? t('estado.cuadra') : t(diferencia > 0 ? 'estado.deMas' : 'estado.deMenos')}
            </p>
            {diferencia !== 0 && <p className="tabular mt-1 text-2xl font-bold">{formato(centavos(Math.abs(diferencia)))}</p>}
          </div>
          {diferencia !== 0 && <p className="text-sm text-slate-400">{t('estado.diferenciaAnotada')}</p>}
        </div>
      ) : null,
      seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => ir(siguienteDe(`${id}:resultado`)) },
    };
    return [pregunta, resultado];
  }

  function pasos(): Paso[] {
    const orden: Paso[] = [];
    orden.push({
      id: 'empezar',
      titulo,
      explicacion: <p>{t('cuadrar.explicacion', { desde: fechaCorta(dias.desde), hasta: fechaCorta(dias.hasta) })}</p>,
      contenido: (
        <ul className="flex flex-col gap-2 text-sm text-slate-300">
          <li className="tarjeta flex items-center gap-3 p-3">
            <Ico icono="recibir" className="size-5 shrink-0 text-marca-300" />
            {sobres.length === 0 ? t('cuadrar.nadaPorRecibir') : t('cuadrar.cajasPorRecibir', { n: sobres.length })}
          </li>
          <li className="tarjeta flex items-center gap-3 p-3">
            <Ico icono="transferencias" className="size-5 shrink-0 text-marca-300" />
            {inicio!.cola.length === 0 ? t('cuadrar.nadaPorVer') : t('cuadrar.transferenciasPorVer', { n: inicio!.cola.length })}
          </li>
          <li className="tarjeta flex items-center gap-3 p-3">
            <Ico icono="contar" className="size-5 shrink-0 text-marca-300" />
            {t('cuadrar.yDespues')}
          </li>
        </ul>
      ),
      seguir: { etiqueta: t('cuadrar.empezar'), icono: 'adelante', alTocar: () => ir(siguienteDe('empezar')) },
    });

    // The envelopes.
    if (sobres.length > 0) {
      orden.push(pasoDeSobres());
      if (elegidos.length > 0) {
        if (!cadaUno) orden.push(...pasosJuntos());
        else {
          orden.push({
            id: 'sobres:cada',
            titulo: t('sobres.cadaTitulo'),
            explicacion: <p>{t('sobres.cadaExplicacion')}</p>,
            // The total has been counted: back is each envelope, not the total again.
            sinAtras: true,
            seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => ir(siguienteDe('sobres:cada')) },
          });
          orden.push(...elegidos.flatMap(pasosDeCaja));
        }
      }
    }

    // What left the safe, before the safe is counted.
    orden.push(
      pasoQueSalio({
        id: 'gastos',
        titulo: t('cuadrar.gastosTitulo'),
        explicacion: t('cuadrar.gastosExplicacion'),
        filas: (semana?.gastos ?? []).map((g) => ({
          clave: g.id,
          texto: g.descripcion,
          detalle: `${etiquetaCategoriaGasto(g.categoria)} · ${fechaCorta(g.fecha)}`,
          monto: formatMoney(g.montoCordobas),
          anular: () => setAnulando({ tipo: 'gasto', gasto: g }),
        })),
        vacio: t('cuadrar.sinGastos'),
        anotar: t((semana?.gastos.length ?? 0) === 0 ? 'cuadrar.anotarGasto' : 'cuadrar.anotarOtro'),
        anotarDetalle: t('cuadrar.anotarDetalle'),
        nada: t((semana?.gastos.length ?? 0) === 0 ? 'cuadrar.noPague' : 'cuadrar.esoEsTodo'),
        abrir: () => {
          setAnotando('gasto');
          gasto.empezar();
        },
      }),
      ...(anotando === 'gasto' ? gasto.pasos : []),
      pasoQueSalio({
        id: 'depositos',
        titulo: t('cuadrar.depositoTitulo'),
        explicacion: t('cuadrar.depositoExplicacion'),
        filas: (semana?.depositos ?? []).map((d) => ({
          clave: d.id,
          texto: fechaCorta(d.fecha),
          ...(d.referencia ? { detalle: t('cuadrar.boleta', { numero: d.referencia }) } : {}),
          monto: [...(d.cordobas > 0 ? [formatMoney(d.cordobas)] : []), ...(d.dolares > 0 ? [enDolares(d.dolares)] : [])].join(' · '),
          anular: () => setAnulando({ tipo: 'deposito', deposito: d }),
        })),
        vacio: t('cuadrar.sinDepositos'),
        anotar: t((semana?.depositos.length ?? 0) === 0 ? 'cuadrar.siDeposite' : 'cuadrar.otroDeposito'),
        anotarDetalle: t('cuadrar.siDepositeDetalle'),
        nada: t((semana?.depositos.length ?? 0) === 0 ? 'cuadrar.noDeposite' : 'cuadrar.noHuboMas'),
        abrir: () => {
          setAnotando('deposito');
          deposito.empezar();
        },
      }),
      ...(anotando === 'deposito' ? deposito.pasos : []),
      pasoQueSalio({
        id: 'dueno',
        titulo: t('cuadrar.duenoTitulo'),
        explicacion: t('cuadrar.duenoExplicacion'),
        filas: (semana?.retiros ?? []).map((m) => ({
          clave: m.id,
          texto: m.motivo ?? t('cuadrar.paraUsted'),
          detalle: fechaCorta(m.fecha),
          monto: formatMoney(m.monto),
          anular: () => setAnulando({ tipo: 'dueno', movimiento: m }),
        })),
        vacio: t('cuadrar.sinRetiros'),
        anotar: t((semana?.retiros.length ?? 0) === 0 ? 'cuadrar.siSaque' : 'cuadrar.otroRetiro'),
        anotarDetalle: t('cuadrar.siSaqueDetalle'),
        nada: t((semana?.retiros.length ?? 0) === 0 ? 'cuadrar.noSaque' : 'cuadrar.noHuboMas'),
        abrir: () => {
          setAnotando('dueno');
          dueno.empezar();
        },
      }),
      ...(anotando === 'dueno' ? dueno.pasos : []),
    );
    if (conPagos) {
      orden.push(
        pasoQueSalio({
          id: 'pagos',
          titulo: t('cuadrar.pagosTitulo'),
          explicacion: t('cuadrar.pagosExplicacion'),
          filas: (semana?.pagos ?? []).map((p) => ({
            clave: p.id,
            texto: p.nombre,
            detalle: p.conLaCompra
              ? t('compras.conLaCompra', { fecha: fechaCorta(p.fecha) })
              : p.conElGasto
                ? t('gastos.conElGasto', { fecha: fechaCorta(p.fecha) })
                : fechaCorta(p.fecha),
            monto: formatMoney(p.monto),
            // One from before payments named themselves cannot be voided.
            ...(p.pago ? { anular: () => setAnulando({ tipo: 'pago', deuda: p.deuda, pago: p.pago!, nombre: p.nombre }) } : {}),
          })),
          vacio: t('cuadrar.sinPagos'),
          anotar: t('cuadrar.anotarPago'),
          anotarDetalle: t('cuadrar.anotarPagoDetalle'),
          nada: t((semana?.pagos.length ?? 0) === 0 ? 'cuadrar.noPagueProveedor' : 'cuadrar.noHuboMas'),
          abrir: () => {
            setPagandoDesde('pagos');
            setAnotando('pago');
            pago.empezar();
          },
        }),
        ...(anotando === 'pago' && pagandoDesde === 'pagos' ? pago.pasos : []),
      );
    }

    // The safe, blind: its first count is where it starts.
    const fuerteCordobas = pasoDeConteo({
      id: 'fuerte:cordobas',
      moneda: 'NIO',
      contando: fuerte.cordobas,
      poner: (x) => setFuerte((f) => ({ ...f, cordobas: x })),
      esperado: null,
      umbral: config.cajaReconteoDesde,
      titulo: t('fuerte.contarCordobas'),
      explicacion: t(inicial ? 'fuerte.primeraVez' : 'fuerte.contarExplicacion'),
      siguiente: () => ir('fuerte:dolares'),
      t,
    });
    orden.push(
      fuerteCordobas,
      pasoDeConteo({
        id: 'fuerte:dolares',
        moneda: 'USD',
        contando: fuerte.dolares,
        poner: (x) => setFuerte((f) => ({ ...f, dolares: x })),
        esperado: null,
        umbral: config.cajaReconteoDesde,
        titulo: t('fuerte.contarDolares'),
        explicacion: t('cuadrar.contarDolaresExplicacion'),
        siguiente: () => ir('fuerte:revise'),
        t,
      }),
      {
        id: 'fuerte:revise',
        titulo: t('turno.revise'),
        explicacion: <p>{t(inicial ? 'fuerte.reviseInicial' : 'fuerte.revise')}</p>,
        contenido: (
          <div className="flex flex-col gap-4">
            <div className="tarjeta flex flex-col gap-2">
              <Dato etiqueta={t('turno.contoCordobas')} valor={formatMoney(fC ?? (0 as Centavos))} />
              <Dato etiqueta={t('turno.contoDolares')} valor={enDolares(fD ?? (0 as Centavos))} />
            </div>
            {error && actual === 'fuerte:revise' && <p className="text-sm text-rose-400">{error}</p>}
          </div>
        ),
        seguir: {
          etiqueta: t('fuerte.guardar'),
          icono: 'guardar',
          deshabilitado: trabajando || fC === null || fD === null || fuerteHecho !== null || libros === undefined,
          alTocar: revisarFuerte,
        },
      },
      {
        id: 'fuerte:resultado',
        titulo: t(fuerteHecho?.inicial ? 'fuerte.empiezaTitulo' : 'fuerte.resultadoTitulo'),
        sinAtras: true,
        explicacion: fuerteHecho && <p>{t(fuerteHecho.inicial ? 'fuerte.empiezaExplicacion' : 'fuerte.resultadoExplicacion')}</p>,
        contenido: fuerteHecho ? (
          <div className="flex flex-col gap-4" data-cuadrar-fuerte>
            {fuerteHecho.inicial ? (
              <div className="tarjeta flex flex-col gap-2">
                <Dato etiqueta={t('turno.contoCordobas')} valor={formatMoney(fuerteHecho.contadoC)} />
                <Dato etiqueta={t('turno.contoDolares')} valor={enDolares(fuerteHecho.contadoD)} />
                <Dato etiqueta={t('fuerte.enTotal')} valor={formatMoney(fuerteHecho.valor)} />
              </div>
            ) : (
              <>
                <Veredicto
                  esperado={t('fuerte.segunLibros')}
                  valorEsperado={formatMoney(fuerteHecho.esperado)}
                  valorContado={formatMoney(fuerteHecho.valor)}
                  diferencia={centavos(fuerteHecho.valor - fuerteHecho.esperado)}
                  cuadra={t('fuerte.cuadra')}
                  formato={(x) => formatMoney(x)}
                  primerConteo={fuerteHecho.primero !== null ? formatMoney(fuerteHecho.primero) : null}
                />
                {fuerteHecho.valor !== fuerteHecho.esperado && (
                  <p className="text-sm text-slate-400">
                    {t(fuerteHecho.valor > fuerteHecho.esperado ? 'fuerte.sobranteAnotado' : 'fuerte.faltanteAnotado')}
                  </p>
                )}
                {fuerteHecho.falto > 0 && (
                  <button type="button" className="btn-secundario" onClick={() => setExplicando(fuerteHecho.eventoId)} data-cuadrar-explicar>
                    <Ico icono="gastos" />
                    {t('explicar.botonFaltante')}
                  </button>
                )}
              </>
            )}
          </div>
        ) : null,
        seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => ir(siguienteDe('fuerte:resultado')) },
      },
    );

    // The transfers, then the statements they are on.
    orden.push(...inicio!.cola.flatMap(pasosDeTransferencia));
    // Each account the shop uses, then the wallet: one with nothing in it has no statement to square.
    const conEstado: Cuenta[] = [...cuentasEnUso(config, bancos ?? {}).map((c) => c.cuenta), 'BILLETERA_MOVIL'];
    for (const c of conEstado) {
      const tiene = saldo(ACCOUNTS[c].code) !== 0 || (c !== 'BILLETERA_MOVIL' && (bancos?.[c]?.dolares ?? 0) !== 0);
      const aqui = actual === `estado:${c}` || actual.startsWith(`estado:${c}:`);
      if (tiene || conciliadas[c] || sinEstado[c] || aqui) orden.push(...pasosDeEstado(c));
    }

    // What is owed to suppliers soon.
    const pronto = semana?.pronto ?? [];
    if (pronto.length > 0 || pagandoAqui('proveedores')) {
      orden.push(
        {
          id: 'proveedores',
          titulo: t('cuadrar.proveedoresTitulo'),
          explicacion: <p>{t('cuadrar.proveedoresExplicacion')}</p>,
          contenido: (
            <div className="flex flex-col gap-4">
              {lista(
                pronto.map((d: PorPagar & { nombre: string }) => ({
                  clave: d.id,
                  texto: d.nombre,
                  detalle: [
                    d.detalle,
                    d.vence ? t(d.vence < hoy() ? 'cuadrar.vencio' : 'pagoPaso.vence', { fecha: fechaCorta(d.vence) }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · '),
                  monto: formatMoney(d.pendiente),
                })),
                t('cuadrar.nadaPronto'),
                'data-cuadrar-proveedores',
              )}
              <Respuestas
                opciones={[
                  {
                    etiqueta: t('cuadrar.pagarUno'),
                    detalle: t('cuadrar.anotarPagoDetalle'),
                    icono: 'pagar',
                    alTocar: () => {
                      setPagandoDesde('proveedores');
                      setAnotando('pago');
                      pago.empezar();
                    },
                  },
                  {
                    etiqueta: t('cuadrar.pagarDespues'),
                    icono: 'adelante',
                    alTocar: () => {
                      setAnotando(null);
                      ir('resumen');
                    },
                  },
                ]}
              />
            </div>
          ),
        },
        ...(anotando === 'pago' && pagandoDesde === 'proveedores' ? pago.pasos : []),
      );
    }

    orden.push({
      id: 'resumen',
      titulo: t(dias.desde === dias.hasta ? 'cuadrar.resumenTituloDia' : 'cuadrar.resumenTitulo'),
      explicacion: <p>{t('cuadrar.resumenExplicacion', { desde: fechaCorta(dias.desde), hasta: fechaCorta(dias.hasta) })}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <LoQueDejoElDia desde={dias.desde} hasta={dias.hasta} />
          {error && actual === 'resumen' && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      ),
      seguir:
        quedan.length > 0
          ? { etiqueta: t('asistente.siguiente'), alTocar: () => ir('pendientes') }
          : { etiqueta: t('cuadrar.terminar'), icono: 'listo', deshabilitado: trabajando, alTocar: () => void terminar() },
    });
    // Kept while it is on screen, though what it lists is seen to meanwhile: gone, the step after
    // it is «¡Listo!», with the week not gone over.
    if (quedan.length > 0 || actual === 'pendientes') {
      orden.push({
        id: 'pendientes',
        titulo: t('cuadrar.pendientesTitulo'),
        explicacion: <p>{t('cuadrar.pendientesExplicacion')}</p>,
        contenido: (
          <div className="flex flex-col gap-3">
            {quedan.length > 0 ? (
              <ListaDeAvisos avisos={quedan} />
            ) : (
              <p className="tarjeta p-3 text-sm text-slate-400">{t('cuadrar.nadaPendiente')}</p>
            )}
            {error && <p className="text-sm text-rose-400">{error}</p>}
          </div>
        ),
        seguir: { etiqueta: t('cuadrar.terminar'), icono: 'listo', deshabilitado: trabajando, alTocar: () => void terminar() },
      });
    }
    orden.push({
      id: 'listo',
      titulo: t('cuadrar.listoTitulo'),
      sinAtras: true,
      explicacion: cuadrado ? <p>{t('cuadrar.listoExplicacion')}</p> : null,
      contenido: cuadrado ? (
        <Link to="/efectivo" replace onClick={atrasHasta('/efectivo')} className="btn-secundario">
          <Ico icono="caja" />
          {t('cuadrar.verEfectivo')}
        </Link>
      ) : null,
      seguir: { etiqueta: t('turno.irAlInicio'), icono: 'inicio', alTocar: () => void irA({ to: '/' }) },
    });
    return orden;
  }

  const todos = pasos();
  const ids = todos.map((p) => p.id);
  const i = indicePaso(ids, actual, ultimo.current);
  ultimo.current = i;
  const paso = todos[i]!;
  // «Atrás» never into what was received, counted or squared, nor out of the week once gone over.
  const puedeVolver = i > 0 && !paso.sinAtras && !(cuadrado && paso.id === 'listo');

  return (
    <>
      <Asistente
        titulo={titulo}
        subtitulo={t('cuadrar.subtitulo')}
        paso={paso}
        numero={i + 1}
        total={todos.length}
        volverA="/"
        {...(puedeVolver ? { onAtras: () => ir(ids[i - 1]!) } : {})}
      />
      {explicando && <ExplicarFaltante eventoId={explicando} onCerrar={() => setExplicando(null)} />}
      {anulando?.tipo === 'gasto' && <AnularGasto gasto={anulando.gasto} onCerrar={() => setAnulando(null)} />}
      {anulando?.tipo === 'deposito' && <AnularDeposito deposito={anulando.deposito} onCerrar={() => setAnulando(null)} />}
      {anulando?.tipo === 'dueno' && <AnularMovimiento movimiento={anulando.movimiento} onCerrar={() => setAnulando(null)} />}
      {anulando?.tipo === 'pago' && (
        <AnularPago deuda={anulando.deuda} pago={anulando.pago} nombre={anulando.nombre} onCerrar={() => setAnulando(null)} />
      )}
    </>
  );
}
